import dayjs from "dayjs";

import { isToday, isYesterday } from "../../../helperFunctions";

export const PAGINATION_LIMIT = 10;
export const SEARCH_DEBOUNCE_MS = 1000;

export const normalizeConversations = conversations =>
  conversations
    .filter(item => !item?.archived)
    .map(item => ({
      id: item.id,
      title: item.title,
      status: item.status,
      createdAt: item.created_at,
      updatedAt: item.updated_at,
      clientName: item?.client?.name,
      clientId: item?.client?.id,
    }))
    .sort(
      (firstItem, secondItem) =>
        dayjs(secondItem.updatedAt).valueOf() -
        dayjs(firstItem.updatedAt).valueOf()
    );

export const getGroupLabel = date => {
  if (isToday(date)) return "TODAY";
  if (isYesterday(date)) return "YESTERDAY";
  return dayjs(date).format("D MMMM YYYY").toUpperCase();
};

export const getLastChatLabel = date => {
  if (!date) return "Last chat: -";

  if (isToday(date) || isYesterday(date)) {
    return `Last chat: ${dayjs(date).format("h:mm A")}`;
  }

  return `Last chat: ${dayjs(date).format("D MMM, YYYY")}`;
};

export const mergeUniqueConversations = (previousHistory, nextHistory) => {
  const conversationMap = new Map();

  [...(previousHistory || []), ...(nextHistory || [])].forEach(conversation => {
    if (!conversation?.id) return;
    conversationMap.set(conversation.id, conversation);
  });

  return Array.from(conversationMap.values());
};



export const getMessageId = (message, index) =>
  message?.id ?? message?._id ?? index;

export const getMessageTimestamp = message =>
  message?.timestamp ?? message?.created_at ?? message?.createdAt;

export const getMessageDateKey = message => {
  const timestamp = getMessageTimestamp(message);
  if (!timestamp) return "today";

  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? "today" : date.toDateString();
};

export const formatMessageDateLabel = dateKey => {
  if (dateKey === "today") return "Today";

  const today = new Date().toDateString();
  if (dateKey === today) return "Today";

  const date = new Date(dateKey);
  return date.toLocaleDateString(undefined, {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

export const shouldShowMessageDateDivider = (currentDateKey, previousDateKey) =>
  currentDateKey !== previousDateKey;

export const getMessageCopyText = message =>
  message?.markdownText || message?.message || "";

export const shouldShowAiMessageFooter = ({
  isAi,
  isLastMessage,
  isStreaming,
  isTypingComplete,
}) => {
  if (!isAi) return false;
  if (!isLastMessage) return true;
  if (isStreaming) return isTypingComplete;
  return true;
};

export const detectMessageListError = safeMessages => {
  const aiMessageWithError = safeMessages.find(
    message => message?.sender === "ai" && (message?.error || message?.isError)
  );

  if (aiMessageWithError) return aiMessageWithError;

  const lastMessage = safeMessages[safeMessages.length - 1];
  const isUserMessage = lastMessage?.sender === "user";

  if (isUserMessage) {
    return {
      sender: "ai",
      error: true,
      errorTitle: "We couldn't generate a response.",
      errorDescription:
        "A response couldn't be generated due to an unexpected error.",
      errorSuggestion:
        "Try rephrasing your prompt. If the issue persists, reach out to support.",
    };
  }

  return null;
};

export const groupHistoryByDate = history => {
  const groupedMap = history.reduce((accumulator, item) => {
    const key = dayjs(item.updatedAt).format("YYYY-MM-DD");

    if (!accumulator[key]) {
      accumulator[key] = [];
    }

    accumulator[key].push(item);

    return accumulator;
  }, {});

  return Object.keys(groupedMap)
    .sort(
      (firstDate, secondDate) =>
        dayjs(secondDate).valueOf() - dayjs(firstDate).valueOf()
    )
    .map(key => ({
      key,
      label: getGroupLabel(groupedMap[key][0]?.updatedAt),
      items: groupedMap[key],
    }));
};

export const createMessage = (sender, markdownText, extra = {}) => ({
  id: `${sender}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  sender,
  markdownText,
  timestamp: Date.now(),
  ...extra,
});

// Handle real-time messages from Centrifugo (streaming chunks and regular messages)
export const mergeFinalContent = (currentText = "", finalText = "") => {
  if (!finalText) return currentText;

  // Exact match → no change
  if (currentText === finalText) {
    return currentText;
  }

  // Streamed content is prefix of final content
  // Append only the missing part
  if (finalText.startsWith(currentText)) {
    return currentText + finalText.slice(currentText.length);
  }

  // Fallback: content differs unexpectedly
  return finalText;
};
